import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { resolveImageUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ArrowLeft, Heart, Loader2, Send, Trash2, MessageCircle } from "lucide-react";

export default function ShowcaseDetailPage() {
  const { showcase_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyVote, setBusyVote] = useState(false);

  useEffect(() => {
    api.get(`/showcases/${showcase_id}`)
      .then((r) => setShowcase(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Showcase not found"))
      .finally(() => setLoading(false));
  }, [showcase_id]);

  const vote = async () => {
    if (!user) return toast.error("Sign in to vote");
    setBusyVote(true);
    try {
      const { data } = await api.post(`/showcases/${showcase_id}/vote`);
      setShowcase((prev) => ({ ...prev, votes_count: data.votes_count, voted_by_viewer: data.voted }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vote failed");
    } finally {
      setBusyVote(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this showcase?")) return;
    try {
      await api.delete(`/showcases/${showcase_id}`);
      toast.success("Deleted");
      navigate("/showcases");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="showcase-detail-loading">
        <Loader2 className="w-8 h-8 text-zinc-500 animate-spin" />
      </div>
    );
  }
  if (!showcase) {
    return (
      <div className="p-8 text-center text-zinc-400" data-testid="showcase-detail-missing">
        Showcase not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={() => navigate("/showcases")}>← Back to Showcases</Button>
        </div>
      </div>
    );
  }

  const isOwner = user && user.user_id === showcase.user_id;

  return (
    <div data-testid="showcase-detail-page" className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto animate-fadeIn">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-zinc-100 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </Button>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
        <div className="bg-zinc-900 ring-1 ring-white/5 rounded-2xl overflow-hidden">
          <img
            src={resolveImageUrl(showcase.photo_url)}
            alt={showcase.title}
            className="w-full max-h-[80vh] object-contain bg-black"
          />
        </div>

        <div className="space-y-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-zinc-100 leading-tight">{showcase.title}</h1>
            <p className="text-zinc-500 text-xs mt-1">
              {new Date(showcase.created_at).toLocaleDateString()}
            </p>
          </div>

          <Link
            to={showcase.user_profile_slug ? `/collector/${showcase.user_profile_slug}` : "#"}
            className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900 ring-1 ring-white/5 hover:ring-rose-500/30 transition-colors"
            data-testid="showcase-detail-author-link"
          >
            {showcase.user_profile_photo ? (
              <img src={resolveImageUrl(showcase.user_profile_photo)} alt="" className="w-9 h-9 rounded-full object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-zinc-800 grid place-items-center text-sm text-zinc-300">
                {(showcase.user_name || "?")[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-zinc-100 text-sm font-semibold">{showcase.user_name}</p>
              {showcase.user_profile_slug && <p className="text-zinc-500 text-xs">@{showcase.user_profile_slug}</p>}
            </div>
          </Link>

          {showcase.caption && (
            <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed bg-zinc-900/60 rounded-xl p-4 ring-1 ring-white/5">
              {showcase.caption}
            </p>
          )}

          {showcase.release && (
            <Link
              to={`/catalog/${showcase.release.release_id}`}
              className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900 ring-1 ring-white/5 hover:ring-rose-500/30"
              data-testid="showcase-detail-release-link"
            >
              {showcase.release.model_image && (
                <img src={resolveImageUrl(showcase.release.model_image)} alt="" className="w-12 h-12 rounded object-contain bg-zinc-800" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-zinc-100 text-sm font-semibold truncate">{showcase.release.release_name}</p>
                <p className="text-zinc-500 text-xs">{showcase.release.brand} · {showcase.release.vehicle}</p>
              </div>
            </Link>
          )}

          <div className="flex items-center gap-2">
            <Button
              onClick={vote}
              disabled={busyVote}
              className={`flex-1 ${
                showcase.voted_by_viewer
                  ? "bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 ring-1 ring-rose-500/30"
                  : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
              }`}
              data-testid="showcase-detail-vote-btn"
            >
              <Heart className={`w-4 h-4 mr-2 ${showcase.voted_by_viewer ? "fill-current" : ""}`} />
              {showcase.voted_by_viewer ? "Voted" : "Vote"} · {showcase.votes_count || 0}
            </Button>
            {isOwner && (
              <Button variant="outline" onClick={remove} className="border-zinc-800 text-zinc-400 hover:text-rose-400" data-testid="showcase-detail-delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Comments thread */}
      <CommentsThread showcaseId={showcase_id} ownerId={showcase.user_id} />
    </div>
  );
}

function CommentsThread({ showcaseId, ownerId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/showcases/${showcaseId}/comments`).then((r) => setItems(r.data.comments || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showcaseId]);

  const post = async () => {
    if (!user) return toast.error("Sign in to comment");
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/showcases/${showcaseId}/comments`, { body: text });
      setItems((prev) => [...prev, data]);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this comment?")) return;
    try {
      await api.delete(`/showcases/${showcaseId}/comments/${id}`);
      setItems((prev) => prev.filter((c) => c.comment_id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="showcase-comments" className="mt-8 max-w-3xl mx-auto">
      <h3 className="font-heading text-base font-semibold text-zinc-100 flex items-center gap-2 mb-4">
        <MessageCircle className="w-4 h-4 text-rose-400" />
        Comments {items.length ? <span className="text-zinc-500 text-sm font-normal">· {items.length}</span> : null}
      </h3>

      {user ? (
        <div className="bg-zinc-900 ring-1 ring-white/5 rounded-xl p-3 mb-4">
          <Textarea
            data-testid="showcase-comment-input"
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 600))}
            placeholder="Add a thoughtful comment…"
            rows={2}
            className="bg-transparent border-0 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-0 resize-none"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-zinc-500 text-xs">{body.length}/600</span>
            <Button
              size="sm"
              onClick={post}
              disabled={busy || !body.trim()}
              className="bg-rose-600 hover:bg-rose-500 text-white"
              data-testid="showcase-comment-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Post
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-zinc-500 text-sm mb-4">Sign in to leave a comment.</p>
      )}

      {items.length === 0 ? (
        <p className="text-zinc-600 text-sm text-center py-6" data-testid="showcase-comments-empty">No comments yet — be the first.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => {
            const canDelete = user && (c.user_id === user.user_id || ownerId === user.user_id || user.role === "admin");
            return (
              <li key={c.comment_id} data-testid={`showcase-comment-${c.comment_id}`} className="flex gap-3 bg-zinc-900/60 ring-1 ring-white/5 rounded-xl p-3">
                <Link to={c.user_profile_slug ? `/collector/${c.user_profile_slug}` : "#"} className="shrink-0">
                  {c.user_profile_photo ? (
                    <img src={resolveImageUrl(c.user_profile_photo)} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-zinc-800 grid place-items-center text-xs text-zinc-300">
                      {(c.user_name || "?")[0]}
                    </div>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Link to={c.user_profile_slug ? `/collector/${c.user_profile_slug}` : "#"} className="text-zinc-100 text-sm font-medium hover:text-rose-400 truncate">
                      {c.user_name}
                    </Link>
                    <span className="text-zinc-600 text-xs">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-zinc-300 text-sm mt-1 whitespace-pre-wrap break-words">{c.body}</p>
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(c.comment_id)}
                    data-testid={`showcase-comment-delete-${c.comment_id}`}
                    className="text-zinc-600 hover:text-rose-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
